import React from "react";
import { values } from "mobx";
import { inject, observer } from "mobx-react";
import { withRouter, Redirect } from "react-router-dom";

import {
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Grid,
  TextField,
  Typography,
  withStyles
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";

import RoomCard from "./RoomCard";
import RoomComparisonCard from "./RoomComparisonCard";

import { UiStoreContext } from "../stores/UiStore";

const styles = theme => ({
  backLinkIconWrapper: {
    lineHeight: 0,
    marginRight: theme.spacing(0.5)
  },
  backLink: {
    color: "rgba(0, 0, 0, 0.4)",
    textDecoration: "none"
  },
  pageName: {
    fontWeight: "bold"
  },
  addRoomButton: {
    margin: theme.spacing(1),
    marginRight: theme.spacing(0)
  },
  progressContainer: {
    display: "flex",
    alignItems: "center",
    justify: "center"
  },
  viewSwitchButtonGroup: {
    backgroundColor: "rgba(102, 102, 102, 1.0)"
  }
});

class Overview extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      listView: false,
      selectedLocationIds: new Set(),
      selectedSeasons: ["2022"]
    };
  }

  setLoading(loading) {
    this.setState({ loading: loading });
  }

  setListView(enabled) {
    this.setState({ listView: enabled });
  }

  addSelectedLocationId(id) {
    this.setState(state => ({
      selectedLocationIds: state.selectedLocationIds.add(id)
    }));
  }

  removeSelectedLocationId(id) {
    this.setState(state => {
      let newSelectedLocationIds = new Set(state.selectedLocationIds);
      newSelectedLocationIds.delete(id);
      return { selectedLocationIds: newSelectedLocationIds };
    });
  }

  render() {
    const classes = this.props.classes;

    const rooms = this.props.rootStore.roomStore.ordered().filter(r => {
      const params = (this.props.match || {}).params || {};

      const locationIds = values(r.locations).map(l => l.id);
      const varietyIds = values(r.varieties).map(v => v.id);
      const season = r.meta.season;

      if (
        (params.location_id && !locationIds.includes(params.location_id)) ||
        (params.variety_id && !varietyIds.includes(params.variety_id))
      ) {
        return false;
      }

      if (
        (this.props.user || {}).is_staff &&
        locationIds.filter(id => this.state.selectedLocationIds.has(id))
          .length === 0
      ) {
        return false;
      }

      if (!this.context.selectedSeasons.includes(season)) {
        return false;
      }

      return true;
    });

    const room_cards = rooms.map((r, index) => (
      <Grid key={r.id} item xs={12}>
        <RoomCard room={r} order={index + 1} />
      </Grid>
    ));

    const seasonOptions = this.props.rootStore.roomStore.seasons();

    const locations = this.props.rootStore.roomStore.locationsForSeasons(
      this.context.selectedSeasons
    );

    if (this.state.loading) {
      return (
        <div className={classes.progressContainer}>
          <CircularProgress className={classes.progress} />
        </div>
      );
    }

    if (
      !(this.props.user || {}).is_staff &&
      ((this.props.match || {}).path || "") === "/" &&
      !((this.props.match || {}).params || {}).variety_id &&
      this.props.rootStore.roomStore.varieties().length > 0
    ) {
      return (
        <Redirect
          to={`/varieties/${this.props.rootStore.roomStore.varieties()[0].id}`}
        />
      );
    }

    return (
      <>
        {(this.props.user || {}).is_staff ? (
          <>
            <Grid
              container
              direction="row"
              justify="space-between"
              alignItems="center"
            >
              <Typography
                style={{
                  fontFamily: "Open Sans",
                  fontWeight: "600",
                  fontSize: "1.25rem",
                  color: "#0A0E34"
                }}
              >
                Customer
              </Typography>
              <Autocomplete
                multiple
                id="season-selector"
                options={seasonOptions}
                onChange={(event, newSeasons) => {
                  this.context.selectedSeasons = newSeasons;
                }}
                value={this.context.selectedSeasons}
                openText="List available seasons"
                style={{ width: 300 }}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Season"
                    variant="outlined"
                    placeholder="Year"
                  />
                )}
              />
            </Grid>
            <Grid
              container
              direction="row"
              justify="flex-start"
              alignItems="center"
              style={{ flexWrap: "wrap" }}
            >
              {locations.map(l => (
                <>
                  <Grid
                    item
                    container
                    direction="row"
                    justify="flex-start"
                    alignItems="center"
                    style={{
                      paddingRight: "18px",
                      width: "auto",
                      flex: "0 0 auto",
                      flexWrap: "nowrap"
                    }}
                  >
                    <Grid item>
                      <Checkbox
                        checked={this.state.selectedLocationIds.has(l.id)}
                        onChange={e =>
                          e.target.checked
                            ? this.addSelectedLocationId(l.id)
                            : this.removeSelectedLocationId(l.id)
                        }
                        color="primary"
                      />
                    </Grid>
                    <Grid item>
                      <Typography noWrap>{l.human_name}</Typography>
                    </Grid>
                  </Grid>
                </>
              ))}
            </Grid>
          </>
        ) : (
          <></>
        )}

        <Grid container spacing={2}>
          <Grid key={"comparison"} item xs={12}>
            <RoomComparisonCard
              key={this.props.match.url}
              rooms={rooms}
              variety={
                this.props.rootStore.roomStore
                  .varieties()
                  .filter(
                    v =>
                      v.id ===
                      ((this.props.match || {}).params || {}).variety_id
                  )[0]
              }
            />
          </Grid>
          {room_cards}
        </Grid>

        {(this.props.user || {}).is_staff ? (
          <>
            <br />
            <Divider />
            <br />

            <Grid container spacing={2} justify="center">
              <Grid item>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => this.props.rootStore.roomStore.downloadJson()}
                >
                  Export Data (JSON)
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => this.props.rootStore.roomStore.downloadCsv()}
                >
                  Export Data (CSV)
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() =>
                    this.props.rootStore.roomStore.downloadByLocationCsv()
                  }
                >
                  Export Data by Location (CSV)
                </Button>
              </Grid>
            </Grid>
          </>
        ) : (
          <></>
        )}
      </>
    );
  }
}

Overview.contextType = UiStoreContext;

export default withRouter(
  withStyles(styles)(
    inject(stores => ({
      rootStore: stores.rootStore,
      user: stores.rootStore.authStore.user
    }))(observer(Overview))
  )
);
